import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import "./DeviceSettings.css";
import "./DroneWaypoints.css";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { ENDPOINTS } from "../../utils/appConfig";
import {
  fetchDeviceConfiguration,
  fetchCameraDetails,
  ensureCameraPath,
  fetchCameraWaypoints,
  saveWaypoint,
  deleteWaypoint,
} from '../../services/deviceService';
import { refreshAccessToken } from '../../services/authService';
import WaypointMap from "./WaypointMap";
import Sidebar from "../shared/Sidebar";
import Header from "../shared/Header";
import LoadingSpinner from "../shared/LoadingSpinner";
import { useTheme } from "../../contexts/ThemeContext";
import { getItem, logout } from "../../utils/authStorage";
import { toast } from "react-toastify";
import {
  TablerX,
  TablerSetting,
  TablerArrowLeft
} from "../shared/Icons";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { renderMultiSectionDigitalClockTimeView } from "@mui/x-date-pickers/timeViewRenderers";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);


function DeviceSettings() {
  const { t } = useTranslation();
  const { deviceId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const deviceName = queryParams.get("name") || "";
  const [collapsed, setCollapsed] = useState(false);
  const username = getItem("username");
  const role = (getItem("role") || "operator").toLowerCase();
  const { isDarkMode } = useTheme();
  const utcToLocalTime = (timeStr) => {
    if (!timeStr) return null;
    const [h, m, s = "00"] = timeStr.split(":").map(Number);
    return dayjs.utc().hour(h).minute(m).second(s).local();
  };

  const handleSignOut = () => {
    logout();
  };

  const hasRoleFn = (allowedRoles) => {
    const storedRole = (getItem("role") || '').toLowerCase();
    return allowedRoles.flat().some(r => typeof r === 'string' && r.toLowerCase() === storedRole);
  };

  const [deviceData, setDeviceData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({ start_time: "", end_time: "", status: "" });
  const [formDataRaw, setFormDataRaw] = useState({ start_time: null, end_time: null });
  const [editingId, setEditingId] = useState(null);
  const [errors, setErrors] = useState({});

  // Drone dispatch path: ordered waypoints the drone flies on alert dispatch
  const DEFAULT_ALTITUDE = 50; // metres — must stay > 0 (RPi aborts on falsy altitude)
  const DEFAULT_HOVER = 5; // seconds — must stay > 0 (RPi aborts on hover_duration <= 0)
  const [points, setPoints] = useState([]); // [{ lat, lng, altitude, hover_duration }]
  const [mapCenter, setMapCenter] = useState(undefined);
  const [savingWaypoints, setSavingWaypoints] = useState(false);
  const [savedWaypointsSnapshot, setSavedWaypointsSnapshot] = useState("[]");
  const isWaypointsDirty = JSON.stringify(points) !== savedWaypointsSnapshot;

  useEffect(() => {
    async function loadWaypoints() {
      try {
        const [camera, wps] = await Promise.all([
          fetchCameraDetails(deviceId).catch(() => null),
          fetchCameraWaypoints(deviceId).catch(() => []),
        ]);
        const camLat = camera && camera.latitude != null ? parseFloat(camera.latitude) : null;
        const camLng = camera && camera.longitude != null ? parseFloat(camera.longitude) : null;
        if (camLat != null && camLng != null) setMapCenter([camLat, camLng]);

        const loadedPoints = (Array.isArray(wps) ? wps : []).map((wp) => ({
          lat: parseFloat(wp.latitude),
          lng: parseFloat(wp.longitude),
          altitude: wp.altitude ?? DEFAULT_ALTITUDE,
          hover_duration: wp.hover_duration ?? DEFAULT_HOVER,
        }));
        setPoints(loadedPoints);
        setSavedWaypointsSnapshot(JSON.stringify(loadedPoints));
      } catch (err) {
        console.error("Failed to load camera waypoints:", err);
      }
    }
    loadWaypoints();
  }, [deviceId]);

  const handleMapAdd = (latlng) => {
    setPoints((prev) => [
      ...prev,
      { lat: latlng.lat, lng: latlng.lng, altitude: DEFAULT_ALTITUDE, hover_duration: DEFAULT_HOVER },
    ]);
  };

  const handleUndoPoint = () => setPoints((prev) => prev.slice(0, -1));
  const handleClearPoints = () => setPoints([]);
  const handleRemovePoint = (index) => setPoints((prev) => prev.filter((_, i) => i !== index));

  const handleWaypointField = (index, field, value) => {
    setPoints((prev) => prev.map((p, i) => (i === index ? { ...p, [field]: value } : p)));
  };

  const handleSaveWaypoints = async () => {
    setSavingWaypoints(true);
    try {
      const pathId = await ensureCameraPath(deviceId, deviceName ? `${deviceName} path` : null);
      const existing = await fetchCameraWaypoints(deviceId);
      await Promise.all((Array.isArray(existing) ? existing : []).map((wp) => deleteWaypoint(wp.id)));
      for (let i = 0; i < points.length; i++) {
        const p = points[i];
        await saveWaypoint({
          latitude: Number(p.lat.toFixed(8)),
          longitude: Number(p.lng.toFixed(8)),
          altitude: p.altitude === "" ? DEFAULT_ALTITUDE : parseFloat(p.altitude),
          hover_duration: p.hover_duration === "" ? DEFAULT_HOVER : parseFloat(p.hover_duration),
          order: i,
          path: pathId,
        });
      }
      setSavedWaypointsSnapshot(JSON.stringify(points));
      toast.success(t("label.waypointsSaved"));
    } catch (error) {
      console.error("Error saving camera waypoints:", error);
      toast.error(t("label.waypointSaveFailed"));
    } finally {
      setSavingWaypoints(false);
    }
  };

  useEffect(() => {
    async function loadDeviceConfig() {
      try {
        const response = await fetchDeviceConfiguration(deviceId);
        setDeviceData(response);
      } catch (err) {
        console.error("Failed to fetch device detail:", err);
      } finally {
        setLoading(false);
      }
    }
    loadDeviceConfig();
  }, [deviceId, reload]);

  const handleTimeChange = (field) => (newValue) => {
    setFormDataRaw((prev) => ({ ...prev, [field]: newValue }));

    if (newValue && newValue.isValid()) {
      const utcStr = newValue.utc().format("HH:mm:ss");
      setFormData((prev) => ({ ...prev, [field]: utcStr }));

      setErrors((prev) => {
        const { [field]: removed, ...rest } = prev;
        return rest;
      });

      if (field === "end_time") {
        const startRaw = formDataRaw.start_time;
        if (startRaw && startRaw.isValid()) {
          const startUTC = startRaw.utc().format("HH:mm:ss");
          if (startUTC >= utcStr) {
            setErrors((prev) => ({ ...prev, end_time: t("validation.endTimeGreater") }));
          }
        }
      }
    } else {
      setFormData((prev) => ({ ...prev, [field]: "" }));
      setErrors((prev) => ({ ...prev, [field]: t("validation.timeInvalid") }));
    }
  };

  const handleSelectChange = (e) => {
    const { id, value } = e.target;
    setFormData((prev) => ({ ...prev, [id]: value }));
  };

  const resetForm = () => {
    setFormData({ start_time: "", end_time: "", status: "" });
    setFormDataRaw({ start_time: null, end_time: null });
    setEditingId(null);
    setErrors({});
    setShowModal(false);
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.start_time) newErrors.start_time = t("validation.startTimeRequired");
    if (!formData.end_time) newErrors.end_time = t("validation.endTimeRequired");
    if (
      formData.start_time &&
      formData.end_time &&
      formData.start_time >= formData.end_time
    ) {
      newErrors.end_time = t("validation.endTimeGreater");
    }
    if (!formData.status) newErrors.status = t("validation.statusRequired");
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };



  const handleAdd = async () => {
    if (!validateForm()) return;
  let accessToken = getItem('accessToken');
    const payload = {
      ...formData,
      device: deviceId,
    };

    const makeRequest = async (token) => {
      return await fetch(ENDPOINTS.DEVICE_CONFIG_POST, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
    };
    try {
      let response = await makeRequest(accessToken);
      if (response.status === 401 || response.status === 403) {
        accessToken = await refreshAccessToken();
        response = await makeRequest(accessToken);
      }
      if (!response.ok) throw new Error(`Error ${response.status}: ${response.statusText}`);
      resetForm();
      setReload((prev) => !prev);
    } catch (error) {
      console.error('Error while adding configuration:', error);
      alert("Error while adding configuration");
    }
  };

  const handleSave = async () => {
    if (!validateForm() || editingId === null) return;
    const payload = {
      ...formData,
      device: deviceId,
    };

    try {
      let accessToken = getItem("accessToken");
      let response = await fetch(ENDPOINTS.DEVICE_CONFIG_PUT(payload.id), {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (response.status === 401 || response.status === 403) {
        accessToken = await refreshAccessToken();
        response = await fetch(ENDPOINTS.DEVICE_CONFIG_PUT(payload.id), {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        });
      }

      if (!response.ok)
        throw new Error(`Error ${response.status}: ${response.statusText}`);

      const updatedConfig = await response.json();
      setDeviceData((prev) =>
        prev.map((device) =>
          device.id === editingId ? updatedConfig : device
        )
      );
      resetForm();
    } catch (error) {
      console.error("Error while saving configuration:", error);
      alert("Error while saving configuration");
    }
  };

  const handleCancel = () => resetForm();

  const handleEdit = (device) => {
    setFormData({ id: device.id, start_time: device.start_time, end_time: device.end_time, status: device.status });
    setFormDataRaw({
      start_time: utcToLocalTime(device.start_time),
      end_time: utcToLocalTime(device.end_time),
    });
    setEditingId(device.id);
    setShowModal(true);
  };

  const muiTheme = (() => {
    return createTheme({
      palette: {
        mode: isDarkMode ? "dark" : "light",
        ...(isDarkMode && {
          background: {
            paper: "#12171e",
            default: "#0a0b0f",
          },
          text: {
            primary: "#e8eaed",
            secondary: "#9aa0aa",
          },
        }),
      },
      components: {
        MuiPaper: {
          styleOverrides: {
            root: {
              backgroundImage: "none",
            },
          },
        },
      },
    });
  })();

  const isEditMode = editingId !== null;
  const formatDateTime = (isoString) => {
    if (!isoString) return "—";
    const date = new Date(isoString);
    if (isNaN(date.getTime())) return "—";
    const dd = String(date.getDate()).padStart(2, '0');
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const yyyy = date.getFullYear();
    let hh = date.getHours();
    const min = String(date.getMinutes()).padStart(2, '0');
    const ampm = hh >= 12 ? 'PM' : 'AM';
    hh = hh % 12 || 12;
    return `${dd}-${mm}-${yyyy} ${String(hh).padStart(2, '0')}:${min} ${ampm}`;
  };

  return (
    <div className={`device-config-page ${isDarkMode ? "dark" : "light"}`}>
      <Sidebar
        username={username}
        onLogout={handleSignOut}
        hasRole={hasRoleFn}
        collapsed={collapsed}
        onToggleCollapse={() => setCollapsed(prev => !prev)}
        isDarkMode={isDarkMode}
        role={role}
      />

      <div className="main-content">
                <Header title={t("label.deviceDetails")} />
        <div className="main-divider" />

        <div className="config-page-container">
          <div className="config-header-row">
            <div className="config-header-left">
              <button
                className="config-back-btn"
                onClick={() => navigate("/device-list")}
                title={t("button.backToList")}
                aria-label="Go Back"
              >
                <TablerArrowLeft size={16} />
              </button>
              <div className="title-area">
                <h3 className="config-title">{t("label.configurationFor", { name: deviceName })}</h3>
                <p className="config-subtitle">
                  {t("label.configSubtitle")}
                </p>
              </div>
            </div>
            {!isEditMode && (
              <button className="add-config-btn" onClick={() => setShowModal(true)}>
                <span className="plus-icon">+</span> {t("button.addRange")}
              </button>
            )}
          </div>

          <div className="config-table-wrapper">
            {loading ? (
              <LoadingSpinner message={t("loading.configData")} />
            ) : !(Array.isArray(deviceData) ? deviceData : []).length ? (
              <div className="no-data-cell" style={{ padding: "32px", textAlign: "center", color: "var(--text-muted)", fontStyle: "italic" }}>
                {t("label.noConfigurations")}
              </div>
            ) : (
              <table className="config-table">
                <thead>
                  <tr>
                    <th>{t("label.startTime")}</th>
                    <th>{t("label.endTime")}</th>
                    <th>{t("label.statusLabel")}</th>
                    <th>{t("label.createdAt")}</th>
                    <th>{t("label.updatedAt")}</th>
                    <th className="action-col-header">{t("label.action")}</th>
                  </tr>
                </thead>
                <tbody>
                  {(Array.isArray(deviceData) ? deviceData : []).map((device, index) => (
                    <tr key={device?.id || index}>
                      <td>{device?.start_time ? utcToLocalTime(device.start_time).format("hh:mm A") : "—"}</td>
                      <td>{device?.end_time ? utcToLocalTime(device.end_time).format("hh:mm A") : "—"}</td>
                      <td>
                        <span className={`status-badge status-${String(device?.status || '').toLowerCase()}`}>
                          {device?.status ? device.status.charAt(0).toUpperCase() + device.status.slice(1) : '—'}
                        </span>
                      </td>
                      <td>{formatDateTime(device?.created_at)}</td>
                      <td>{formatDateTime(device?.updated_at)}</td>
                      <td className="action-cell">
                        <button className="edit-action-btn" onClick={() => handleEdit(device)} title={t("button.editConfiguration")}>
                          <TablerSetting size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div className="waypoint-section">
            <div className="config-header-row">
              <div className="config-header-left">
                <div className="title-area">
                  <h3 className="config-title">{t("label.dispatchWaypoint")}</h3>
                  <p className="config-subtitle">{t("label.dispatchWaypointHint")}</p>
                </div>
              </div>
              <div className="waypoint-toolbar">
                <button
                  className="waypoint-btn"
                  onClick={handleUndoPoint}
                  disabled={!points.length || savingWaypoints}
                >
                  {t("button.undoPoint")}
                </button>
                <button
                  className="waypoint-btn"
                  onClick={handleClearPoints}
                  disabled={!points.length || savingWaypoints}
                >
                  {t("button.clearPoints")}
                </button>
                <button
                  className="add-config-btn"
                  onClick={handleSaveWaypoints}
                  disabled={savingWaypoints || !points.length || !isWaypointsDirty}
                >
                  {savingWaypoints ? t("button.saving") : t("button.savePath")}
                </button>
              </div>
            </div>

            <div className="waypoint-layout">
              <div className="waypoint-map-wrapper">
                <WaypointMap points={points} onAdd={handleMapAdd} center={mapCenter} />
              </div>

              <div className="waypoint-list">
                <div className="waypoint-list-header">{t("label.flightOrder")}</div>
                {!points.length ? (
                  <div className="waypoint-empty">{t("label.clickMapToSetCamera")}</div>
                ) : (
                  <ul className="waypoint-items">
                    {points.map((p, index) => (
                      <li className="waypoint-item" key={index}>
                        <span className={`waypoint-seq ${index === 0 ? "waypoint-seq-start" : ""}`}>
                          {index + 1}
                        </span>
                        <div className="waypoint-coords">
                          <span>{p.lat.toFixed(6)}</span>
                          <span>{p.lng.toFixed(6)}</span>
                        </div>
                        <div className="waypoint-inputs">
                          <label>
                            {t("label.altitude")}
                            <input
                              type="number"
                              step="any"
                              value={p.altitude}
                              onChange={(e) => handleWaypointField(index, "altitude", e.target.value)}
                            />
                          </label>
                          <label>
                            {t("label.hoverDuration")}
                            <input
                              type="number"
                              step="any"
                              value={p.hover_duration}
                              onChange={(e) => handleWaypointField(index, "hover_duration", e.target.value)}
                            />
                          </label>
                        </div>
                        <button
                          className="waypoint-remove"
                          onClick={() => handleRemovePoint(index)}
                          title={t("button.deleteWaypoint")}
                        >
                          <TablerX size={15} />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        </div>

        {showModal && (
          <div className="config-modal-overlay">
            <div className="config-modal-content">
              <div className="config-modal-header">
                <h3>{isEditMode ? t("button.editConfiguration") : t("button.addNewConfiguration")}</h3>
                <button className="config-modal-close" onClick={handleCancel} title={t("button.close")}>
                  <TablerX size={18} />
                </button>
              </div>

              <div className="config-modal-body">
                <div className="config-form-group">
                  <label>{t("label.deviceNameLabel")}</label>
                  <input type="text" value={deviceName} disabled className="disabled-input" />
                </div>

              <ThemeProvider theme={muiTheme}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <div className="config-form-group">
                        <label htmlFor="start_time">{t("label.startTime")} <span className="required-star">*</span></label>
                        <TimePicker
                          value={formDataRaw.start_time}
                          onChange={handleTimeChange("start_time")}
                          ampm={true}
                          timeSteps={{ minutes: 1 }}
                          viewRenderers={{
                            hours: renderMultiSectionDigitalClockTimeView,
                            minutes: renderMultiSectionDigitalClockTimeView,
                          }}
                          slotProps={{
                            textField: {
                              id: "start_time",
                              size: "small",
                              fullWidth: true,
                            },
                          }}
                        />
                        {errors.start_time && <div className="field-error-msg">{errors.start_time}</div>}
                      </div>

                      <div className="config-form-group">
                        <label htmlFor="end_time">{t("label.endTime")} <span className="required-star">*</span></label>
                        <TimePicker
                          value={formDataRaw.end_time}
                          onChange={handleTimeChange("end_time")}
                          ampm={true}
                          timeSteps={{ minutes: 1 }}
                          viewRenderers={{
                            hours: renderMultiSectionDigitalClockTimeView,
                            minutes: renderMultiSectionDigitalClockTimeView,
                          }}
                          slotProps={{
                            textField: {
                              id: "end_time",
                              size: "small",
                              fullWidth: true,
                            },
                          }}
                        />
                        {errors.end_time && <div className="field-error-msg">{errors.end_time}</div>}
                      </div>
                    </LocalizationProvider>
                  </ThemeProvider>

                <div className="config-form-group">
                  <label htmlFor="status">{t("label.statusLabel")} <span className="required-star">*</span></label>
                  <select id="status" value={formData.status} onChange={handleSelectChange}>
                    <option value="">{t("selectOption.selectStatus")}</option>
                    <option value="enabled">{t("selectOption.enabled")}</option>
                    <option value="disabled">{t("selectOption.disabled")}</option>
                  </select>
                  {errors.status && <div className="field-error-msg">{errors.status}</div>}
                </div>
              </div>

              <div className="config-modal-footer">
                <button className="modal-btn modal-btn-cancel" onClick={handleCancel}>
                  {t("button.cancel")}
                </button>
                <button className="modal-btn modal-btn-submit" onClick={isEditMode ? handleSave : handleAdd}>
                  {isEditMode ? t("button.saveChanges") : t("button.add")}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default DeviceSettings;
